/**
 * @file WithCMMSOverview.jsx
 * @description 설비보전관리시스템(CMMS) 정식 소개 페이지입니다.
 */

import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Calendar,
  ClipboardCheck,
  Factory,
  Gauge,
  Package,
  Smartphone,
} from 'lucide-react';

const CAPABILITIES = [
  {
    num: '01',
    category: 'ASSET MASTER',
    title: '설비 자산관리',
    Icon: Factory,
    desc: '설비 마스터, 위치, 제조사, 점검 주기, 보전 이력을 설비 단위로 축적해 반복 고장의 원인을 빠르게 찾습니다.',
  },
  {
    num: '02',
    category: 'WORK ORDER',
    title: '작업지시 관리',
    Icon: ClipboardCheck,
    desc: '고장 접수부터 담당자 배정, 작업 체크리스트, 완료 승인까지 보전 업무의 진행 상태를 한 화면에서 관리합니다.',
  },
  {
    num: '03',
    category: 'PREVENTIVE MAINTENANCE',
    title: '예방보전 계획',
    Icon: Calendar,
    desc: '설비별 주기와 가동 데이터를 기준으로 PM 작업을 자동 생성해 사후 대응 중심의 업무를 예방 중심으로 전환합니다.',
  },
  {
    num: '04',
    category: 'PARTS & KPI',
    title: '부품·비용·KPI',
    Icon: Package,
    desc: '예비품 재고, 교체 이력, 보전 비용, MTBF·MTTR·가동률을 연결해 보전 성과를 데이터로 확인합니다.',
  },
];

const DASHBOARD_METRICS = [
  { label: '설비 가동률', value: '96.8%', tone: 'green' },
  { label: '미완료 작업', value: '12건', tone: 'blue' },
  { label: 'PM 준수율', value: '91%', tone: 'amber' },
  { label: '평균 수리시간', value: '2.4h', tone: 'violet' },
];

const WORK_ORDERS = [
  { eq: '믹싱 탱크 #03', type: '예방점검', owner: '김정비', state: '진행' },
  { eq: '포장 라인 #02', type: '고장조치', owner: '이현장', state: '긴급' },
  { eq: '공조기 AHU-1', type: '필터교체', owner: '박설비', state: '예약' },
];

const PROCESS_STEPS = [
  { n: '01', title: '고장 접수', desc: '현장 요청과 알림을 작업지시로 전환' },
  { n: '02', title: '작업 배정', desc: '담당자, 우선순위, 체크리스트 지정' },
  { n: '03', title: '부품 출고', desc: '예비품 재고와 교체 이력을 연결' },
  { n: '04', title: '정비 완료', desc: '조치 결과, 사진, 비용, 시간 기록' },
  { n: '05', title: 'KPI 분석', desc: 'MTBF, MTTR, 가동률 리포트 생성' },
];

function CMMSAssetDiagram() {
  return (
    <svg viewBox="0 0 520 400" className="cmms-hub-svg" aria-label="CMMS 설비보전 현장 관제 다이어그램">
      <defs>
        <linearGradient id="cmms-panel-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f7f8f5" />
        </linearGradient>
      </defs>

      <rect x="10" y="14" width="500" height="372" rx="0" fill="#fff" stroke="#c8bfb0" />
      <rect x="24" y="28" width="472" height="344" fill="#fff" />

      <g className="cmms-svg-panel">
        <rect x="42" y="56" width="218" height="258" fill="url(#cmms-panel-grad)" stroke="#216b52" strokeWidth="2" />
        <rect x="58" y="78" width="74" height="74" fill="#fff" stroke="#216b52" strokeWidth="1.5" />
        <rect x="154" y="78" width="74" height="74" fill="#fff" stroke="#2c4a7a" strokeWidth="1.5" />
        <rect x="58" y="174" width="74" height="74" fill="#fff" stroke="#c98a2b" strokeWidth="1.5" />
        <rect x="154" y="174" width="74" height="74" fill="#fff" stroke="#216b52" strokeWidth="1.5" />
        <path d="M95 152v22M191 152v22M132 115h22M132 211h22" stroke="#bfb0a0" strokeWidth="1.5" strokeDasharray="4 6" />

        <text x="151" y="293" textAnchor="middle" fontSize="12" fontWeight="800" fill="#216b52" fontFamily="inherit">
          설비 상태 맵
        </text>
        <text x="95" y="110" textAnchor="middle" fontSize="10" fontWeight="800" fill="#216b52" fontFamily="inherit">MIX</text>
        <text x="95" y="125" textAnchor="middle" fontSize="8" fill="#857e6e" fontFamily="inherit">정상</text>
        <text x="191" y="110" textAnchor="middle" fontSize="10" fontWeight="800" fill="#2c4a7a" fontFamily="inherit">PACK</text>
        <text x="191" y="125" textAnchor="middle" fontSize="8" fill="#857e6e" fontFamily="inherit">점검</text>
        <text x="95" y="206" textAnchor="middle" fontSize="10" fontWeight="800" fill="#c98a2b" fontFamily="inherit">AHU</text>
        <text x="95" y="221" textAnchor="middle" fontSize="8" fill="#857e6e" fontFamily="inherit">예약</text>
        <text x="191" y="206" textAnchor="middle" fontSize="10" fontWeight="800" fill="#216b52" fontFamily="inherit">LINE</text>
        <text x="191" y="221" textAnchor="middle" fontSize="8" fill="#857e6e" fontFamily="inherit">가동</text>

        <circle cx="132" cy="78" r="5" fill="#216b52">
          <animate attributeName="opacity" values="0.3;1;0.3" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="228" cy="174" r="5" fill="#c98a2b">
          <animate attributeName="opacity" values="0.25;1;0.25" dur="1.8s" repeatCount="indefinite" />
        </circle>
      </g>

      <g>
        <path d="M260 122H302" stroke="#bfb0a0" strokeWidth="1.5" strokeDasharray="5 7" />
        <path d="M260 218H302" stroke="#bfb0a0" strokeWidth="1.5" strokeDasharray="5 7" />
        <circle r="3" fill="#216b52">
          <animateMotion dur="2.6s" repeatCount="indefinite" path="M260 122H302" />
        </circle>
        <circle r="3" fill="#c98a2b">
          <animateMotion dur="2.2s" repeatCount="indefinite" path="M260 218H302" />
        </circle>
      </g>

      <g className="cmms-svg-board">
        <rect x="302" y="54" width="166" height="92" fill="#fff" stroke="#216b52" strokeWidth="1.5" />
        <text x="320" y="80" fontSize="11" fontWeight="800" fill="#216b52" fontFamily="inherit">WORK ORDER</text>
        <rect x="320" y="96" width="96" height="8" fill="#216b52" opacity="0.18" />
        <rect x="320" y="114" width="126" height="8" fill="#216b52" opacity="0.12" />
        <rect x="320" y="130" width="54" height="8" fill="#c98a2b" opacity="0.35" />
        <circle cx="440" cy="82" r="11" fill="#216b52" opacity="0.12" />
        <path d="M435 82l4 4 7-9" stroke="#216b52" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />

        <rect x="302" y="166" width="166" height="78" fill="#fff" stroke="#c98a2b" strokeWidth="1.5" />
        <text x="320" y="193" fontSize="11" fontWeight="800" fill="#c98a2b" fontFamily="inherit">PM SCHEDULE</text>
        <path d="M322 211H448M322 226H414" stroke="#c98a2b" strokeWidth="7" strokeOpacity="0.16" />
        <rect x="322" y="207.5" width="68" height="7" fill="#c98a2b" fillOpacity="0.52">
          <animate attributeName="width" values="42;96;68;116;42" dur="3s" repeatCount="indefinite" />
        </rect>
        <rect x="322" y="222.5" width="38" height="7" fill="#c98a2b" fillOpacity="0.52">
          <animate attributeName="width" values="30;62;38;86;30" dur="3s" begin="0.25s" repeatCount="indefinite" />
        </rect>

        <rect x="302" y="264" width="166" height="64" fill="#fff" stroke="#2c4a7a" strokeWidth="1.5" />
        <text x="320" y="291" fontSize="11" fontWeight="800" fill="#2c4a7a" fontFamily="inherit">MTBF · MTTR</text>
        <path
          className="cmms-svg-kpi-line"
          d="M322 312l28-18 28 10 30-24 38 8"
          fill="none"
          stroke="#2c4a7a"
          strokeWidth="2.2"
          pathLength="1"
        />
        <circle className="cmms-svg-kpi-dot" cx="446" cy="288" r="4" fill="#2c4a7a" />
      </g>

      <rect x="192" y="336" width="136" height="32" fill="#fff" stroke="#216b52" strokeWidth="1.6" />
      <text
        x="260"
        y="356"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="17"
        fontWeight="800"
        fill="rgb(33, 107, 82)"
        fontFamily="inherit"
      >
        CMMS
      </text>
    </svg>
  );
}

function CapabilityCard({ item }) {
  const Icon = item.Icon;

  return (
    <article className="cmms-cap-card">
      <span className="cmms-cap-num">{item.num}</span>
      <Icon size={28} color="#216b52" strokeWidth={1.8} aria-hidden="true" />
      <p className="cmms-cap-category">{item.category}</p>
      <h3 className="cmms-cap-title">{item.title}</h3>
      <p className="cmms-cap-desc">{item.desc}</p>
    </article>
  );
}

function CMMSDashboard() {
  return (
    <div className="cmms-dashboard" aria-label="CMMS 설비보전 대시보드">
      <div className="cmms-dashboard-top">
        <div>
          <p className="cmms-dash-kicker">PLANT A</p>
          <h3>설비보전 현황</h3>
        </div>
        <span className="cmms-live-badge"><Activity size={13} /> LIVE</span>
      </div>

      <div className="cmms-metric-grid">
        {DASHBOARD_METRICS.map((metric) => (
          <div key={metric.label} className={`cmms-metric is-${metric.tone}`}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </div>
        ))}
      </div>

      <div className="cmms-work-table">
        <div className="cmms-table-head">
          <span>설비</span>
          <span>작업유형</span>
          <span>담당</span>
          <span>상태</span>
        </div>
        {WORK_ORDERS.map((row) => (
          <div key={`${row.eq}-${row.type}`} className="cmms-table-row">
            <span>{row.eq}</span>
            <span>{row.type}</span>
            <span>{row.owner}</span>
            <span className={row.state === '긴급' ? 'is-urgent' : ''}>{row.state}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WithCMMSHero() {
  const specs = [
    { label: 'ASSET', value: '설비 마스터 · 보전 이력 · 위치' },
    { label: 'WORK', value: '접수 · 배정 · 점검 · 완료 승인' },
    { label: 'KPI', value: '가동률 · MTBF · MTTR · 보전 비용' },
  ];

  return (
    <section className="cmms-hero">
      <div className="container">
        <div className="cmms-hero-inner">
          <div className="cmms-hero-diagram">
            <CMMSAssetDiagram />
          </div>

          <div className="cmms-hero-text">
            <p className="cmms-eyebrow">— COMPUTERIZED MAINTENANCE MANAGEMENT SYSTEM</p>
            <h1 className="cmms-hero-title">
              현장의 정비를 <em className="cmms-accent">데이터</em>로 잇다.
            </h1>
            <p className="cmms-hero-desc">
              <strong>CMMS</strong>는 설비 자산 · 작업지시 · 정비계획 · 부품재고를 하나의 운영그래프로 묶는 설비보전관리시스템입니다.
              종이 점검표와 흩어진 엑셀 대신, 라인 단위의 가동 데이터와 정비 이력을 한 화면에서 추적하고, 비계획 다운타임을 사전에 차단합니다.
            </p>

            <div className="cmms-specs">
              {specs.map((sp) => (
                <div key={sp.label} className="cmms-spec-row">
                  <span className="cmms-spec-label">{sp.label}</span>
                  <span className="cmms-spec-value">{sp.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CMMSCapabilities() {
  return (
    <section className="cmms-caps">
      <div className="container">
        <div className="cmms-caps-header">
          <p className="cmms-eyebrow">CORE CAPABILITIES</p>
          <h2 className="cmms-caps-title">
            설비보전 업무를 움직이는 <em className="cmms-accent">네 가지 기능</em>
          </h2>
          <p className="cmms-caps-intro">
            CMMS는 설비 자산, 작업지시, 예방보전, 부품·KPI 데이터를 하나의 체계로 연결해 보전팀의 일상 업무를 표준화합니다.
          </p>
        </div>

        <div className="cmms-caps-grid">
          {CAPABILITIES.map((cap) => (
            <CapabilityCard key={cap.num} item={cap} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CMMSDashboardSection() {
  return (
    <section className="cmms-proposal cmms-proposal-dashboard">
      <div className="container">
        <div className="cmms-proposal-inner">
          <div className="cmms-proposal-text">
            <p className="cmms-eyebrow">REAL-TIME MAINTENANCE DASHBOARD</p>
            <h2 className="cmms-proposal-title">
              가동은 더 길게, <em className="cmms-accent">멈춤은 더 짧게</em>
            </h2>
            <p className="cmms-proposal-desc">
              설비 가동률, 미완료 작업, PM 준수율, 평균 수리시간을 한 화면에 모아 보전 우선순위와 현장 병목을 빠르게 판단합니다.
            </p>

            <div className="cmms-value-list">
              <div><Gauge size={18} /> 핵심 KPI를 첫 화면에서 확인</div>
              <div><AlertTriangle size={18} /> 긴급 작업과 지연 작업을 즉시 구분</div>
              <div><Smartphone size={18} /> 모바일 현장 처리 흐름까지 자연스럽게 확장</div>
            </div>
          </div>

          <CMMSDashboard />
        </div>
      </div>
    </section>
  );
}

function CMMSProcessSection() {
  return (
    <section className="cmms-proposal cmms-proposal-process">
      <div className="container">
        <div className="cmms-process-head">
          <p className="cmms-eyebrow">MAINTENANCE WORKFLOW</p>
          <h2 className="cmms-proposal-title">
            한 번의 <em className="cmms-accent">정비 사이클</em>이 흐르는 길
          </h2>
          <p className="cmms-proposal-desc">
            고장 접수부터 작업 배정, 부품 출고, 정비 완료, KPI 분석까지 보전 업무의 전 과정을 하나의 디지털 흐름으로 관리합니다.
          </p>
        </div>

        <div className="cmms-process-steps">
          <div className="cmms-process-line" aria-hidden="true" />
          {PROCESS_STEPS.map((step) => (
            <article key={step.n} className="cmms-process-step">
              <span className="cmms-process-num">{step.n}</span>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </article>
          ))}
        </div>

        <div className="cmms-before-after">
          <div className="cmms-compare-col">
            <p className="cmms-compare-label">Before</p>
            <h3>수기 점검과 사후 대응</h3>
            <ul>
              <li>설비 이력 분산</li>
              <li>예비품 부족과 긴급 구매</li>
              <li>반복 고장 원인 파악 지연</li>
            </ul>
          </div>
          <div className="cmms-compare-arrow" aria-hidden="true">
            <ArrowRight size={24} />
          </div>
          <div className="cmms-compare-col is-after">
            <p className="cmms-compare-label">After</p>
            <h3>디지털 작업지시와 예방보전</h3>
            <ul>
              <li>설비별 보전 이력 통합</li>
              <li>부품 재고와 비용 최적화</li>
              <li>KPI 기반 보전 의사결정</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function CMMSCTA() {
  return (
    <section className="cmms-cta">
      <div className="container">
        <div className="cmms-launch-note">
          <p className="cmms-eyebrow">COMING SOON</p>
          <h2 className="cmms-cta-title">
            <em className="cmms-accent">2027년</em> 정식 런칭 예정
          </h2>
        </div>
      </div>
    </section>
  );
}

export default function WithCMMSOverview() {
  return (
    <>
      <WithCMMSHero />
      <CMMSCapabilities />
      <CMMSDashboardSection />
      <CMMSProcessSection />
      <CMMSCTA />
    </>
  );
}
